// client/src/pages/MealPlanPage.jsx - Page de plan de repas
import React, { useContext, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import AuthContext from '../context/AuthContext';
import { getMealPlanById, updateMealPlan } from '../api/mealApi';
import RecipeCard from '../components/RecipeCard';
import Seo from '../components/Seo';

const MealPlanPage = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [mealPlan, setMealPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDay, setActiveDay] = useState(0);

  let periodLabel = null;
  if (mealPlan?.startDate && mealPlan?.endDate) {
    try {
      const start = format(new Date(mealPlan.startDate), 'dd MMMM yyyy', { locale: fr });
      const end = format(new Date(mealPlan.endDate), 'dd MMMM yyyy', { locale: fr });
      periodLabel = `du ${start} au ${end}`;
    } catch (_) {
      periodLabel = null;
    }
  }

  const seo = (
    <Seo
      title={periodLabel ? `Plan de repas ${periodLabel}` : 'Plan de repas'}
      description={periodLabel
        ? `Parcourez votre plan de repas ${periodLabel} généré avec YAQAM et accédez aux recettes associées.`
        : 'Consultez vos plans de repas personnalisés générés avec YAQAM.'}
      breadcrumb
      noindex={!mealPlan}
    />
  );
  
  useEffect(() => {
    const fetchMealPlan = async () => {
      try {
        setLoading(true);
        const response = await getMealPlanById(id);
        setMealPlan(response.data.data);
        
        // Définir le jour actif sur le jour courant si possible
        const today = new Date();
        const dayIndex = response.data.data.days.findIndex(day => {
          const dayDate = new Date(day.date);
          return (
            dayDate.getDate() === today.getDate() &&
            dayDate.getMonth() === today.getMonth() &&
            dayDate.getFullYear() === today.getFullYear()
          );
        });
        
        if (dayIndex !== -1) {
          setActiveDay(dayIndex);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du plan de repas:', error);
        setError('Impossible de charger ce plan de repas');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMealPlan();
  }, [id]);
  
  const handleFeedback = async (dayIndex, mealIndex, feedbackData) => {
    try {
      await updateMealPlan(id, {
        dayIndex,
        mealIndex,
        ...feedbackData,
      });
      
      // Mettre à jour l'état local
      setMealPlan(prevMealPlan => {
        const updatedMealPlan = { ...prevMealPlan };
        const day = updatedMealPlan.days[dayIndex];
        const meal = day.meals[mealIndex];
        
        if (feedbackData.rating) {
          meal.feedback = { ...meal.feedback, rating: feedbackData.rating };
        }
        
        if (feedbackData.comments) {
          meal.feedback = { ...meal.feedback, comments: feedbackData.comments };
        }
        
        if (feedbackData.isCompleted !== undefined) {
          meal.isCompleted = feedbackData.isCompleted;
        }
        
        return updatedMealPlan;
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du feedback:', error);
    }
  };
  
  if (loading) {
    return (
      <>
        {seo}
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#87A96B] mx-auto mb-4"></div>
            <p className="text-[#2C3E50]">Chargement du plan de repas...</p>
          </div>
        </div>
      </>
    );
  }
  
  if (error) {
    return (
      <>
        {seo}
        <div className="bg-red-100 text-red-800 p-4 rounded-lg border border-red-200">
          {error}
        </div>
      </>
    );
  }
  
  if (!mealPlan) {
    return (
      <>
        {seo}
        <div className="text-center py-8">
          <p className="text-[#2C3E50] mb-4">Plan de repas non trouvé</p>
          <Link 
            to="/dashboard" 
            className="text-[#2C3E50] hover:text-[#2C3E50]/70 font-medium transition-colors"
          >
            Retour au tableau de bord
          </Link>
        </div>
      </>
    );
  }
  
  const startDate = new Date(mealPlan.startDate);
  const endDate = new Date(mealPlan.endDate);
  
  const formatDate = (date) => {
    return format(new Date(date), 'EEEE dd MMMM', { locale: fr });
  };
  
  return (
    <>
      {seo}
      <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <Link
          to="/dashboard"
          className="text-[#2C3E50] hover:text-[#2C3E50]/70 flex items-center font-medium transition-colors"
        >
          ← Retour au tableau de bord
        </Link>
        
        <div className="mt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <h1 className="text-3xl font-bold text-[#2C3E50]">
            Plan du {format(startDate, 'dd/MM')} au {format(endDate, 'dd/MM/yyyy')}
          </h1>
          
          <Link
            to={`/shopping-lists/${mealPlan.shoppingList}`}
            className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#E67E22]/90 transition-colors font-medium shadow-md hover:shadow-lg"
          >
            Voir la liste de courses
          </Link>
        </div>
      </div>
      
      {/* Navigation par jours */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex space-x-2 min-w-max pb-2">
          {mealPlan.days.map((day, index) => (
            <button
              key={index}
              onClick={() => setActiveDay(index)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap font-medium transition-colors ${
                activeDay === index
                  ? 'bg-[#87A96B] text-white shadow-md'
                  : 'bg-gray-100 text-[#2C3E50] hover:bg-[#87A96B]/10 hover:text-[#2C3E50]'
              }`}
            >
              {formatDate(day.date)}
            </button>
          ))}
        </div>
      </div>
      
      {/* Affichage des repas du jour sélectionné */}
      <div className="space-y-8">
        {mealPlan.days[activeDay].meals.map((meal, mealIndex) => (
          <div key={mealIndex} className="bg-white rounded-lg shadow-md border border-gray-100 p-6">
            <h2 className="text-2xl font-semibold mb-4 capitalize text-[#2C3E50] border-b border-[#87A96B]/20 pb-2">
              {meal.type}
            </h2>
            
            <RecipeCard
              recipe={meal.recipe}
              isPremium={['premium','unlimited'].includes(user.subscription.type)}
              onFeedback={(feedbackData) =>
                handleFeedback(activeDay, mealIndex, feedbackData)
              }
            />
          </div>
        ))}
      </div>

      </div>
    </>
  );
};

export default MealPlanPage;
